import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { House, Camera, Package, ChatCircleDots, Users, Briefcase, SignOut, Leaf, Shield, Lock } from "@phosphor-icons/react";
import { clearAuth, getUser } from "../lib/nurseryApi";

const baseItems = [
  { to: "/app/dashboard", label: "Dashboard", icon: House, testid: "nav-dashboard" },
  { to: "/app/scanner", label: "Plant Scanner", icon: Camera, testid: "nav-scanner" },
  { to: "/app/inventory", label: "Inventory", icon: Package, testid: "nav-inventory" },
  { to: "/app/chatbot", label: "Customer Chat", icon: ChatCircleDots, testid: "nav-chatbot", upsell: true },
  { to: "/app/leads", label: "Leads", icon: Users, testid: "nav-leads" },
  { to: "/app/sales-kit", label: "Sales Kit", icon: Briefcase, testid: "nav-sales-kit" },
];

export default function AppLayout({ children }) {
  const nav = useNavigate();
  const user = getUser();

  const logout = () => {
    clearAuth();
    nav("/login");
  };

  return (
    <div className="organic-bg" style={{ minHeight: "100vh" }}>
      {/* Top bar */}
      <header style={{ borderBottom: "1px solid var(--border)", background: "rgba(255,255,255,0.85)", backdropFilter: "saturate(160%) blur(10px)", position: "sticky", top: 0, zIndex: 30 }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          <a href="/" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 10 }} data-testid="brand-link">
            <div style={{ width: 38, height: 38, background: "var(--primary)", borderRadius: 12, display: "grid", placeItems: "center", boxShadow: "var(--shadow-sm)" }}>
              <Leaf size={20} color="white" weight="fill" />
            </div>
            <div>
              <div className="font-display" style={{ fontSize: 20, lineHeight: 1 }}>NurserySense</div>
              <div style={{ fontSize: 10.5, color: "var(--muted-fg)", marginTop: 2 }}>McMinnville · TN</div>
            </div>
          </a>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ textAlign: "right" }} className="hide-sm">
              <div style={{ fontSize: 13, fontWeight: 700 }} data-testid="header-nursery-name">{user?.nursery_name || "Nursery"}</div>
              <div style={{ fontSize: 11, color: "var(--muted-fg)" }}>{user?.owner_name}</div>
            </div>
            <button className="btn btn-ghost" onClick={logout} data-testid="logout-btn">
              <SignOut size={16} weight="bold" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: 1400, margin: "0 auto", display: "grid", gridTemplateColumns: "240px 1fr", gap: 0 }} className="layout-grid">
        <aside style={{ padding: "24px 16px", minHeight: "calc(100vh - 70px)" }} className="sidebar">
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--muted-fg)", marginBottom: 12, padding: "0 10px" }}>Workspace</div>
          <nav style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {baseItems.map(({ to, label, icon: Icon, testid, upsell }) => {
              const isLocked = upsell && !user?.chatbot_enabled;
              return (
                <NavLink key={to} to={to} data-testid={testid}
                  className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                  style={isLocked ? { opacity: 0.65 } : undefined}>
                  <Icon size={18} weight="bold" /> {label}
                  {isLocked && <Lock size={12} weight="bold" style={{ marginLeft: "auto" }} title="Add-on" />}
                </NavLink>
              );
            })}
          </nav>
          <div style={{ marginTop: 28, padding: 16, background: "var(--secondary-soft)", borderRadius: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--secondary)", letterSpacing: "0.1em", textTransform: "uppercase" }}>Demo account</div>
            <div style={{ fontSize: 12.5, marginTop: 6, color: "var(--text-soft)", lineHeight: 1.5 }}>Showing seeded data for <b>Cumberland Ridge Nursery</b>. Add your own anytime.</div>
          </div>
          {user?.is_admin && (
            <NavLink to="/admin" data-testid="nav-admin" style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 10, padding: "11px 14px", borderRadius: 14, textDecoration: "none", background: "var(--text)", color: "var(--accent)", fontWeight: 700, fontSize: 14 }}>
              <Shield size={16} weight="fill" /> Admin panel
            </NavLink>
          )}
        </aside>

        <main style={{ padding: "32px 32px 80px" }} className="main-area">
          {children}
        </main>
      </div>
      <style>{`
        @media (max-width: 900px) {
          .layout-grid { grid-template-columns: 1fr !important; }
          .sidebar { border-bottom: 1px solid var(--border) !important; min-height: auto !important; padding: 16px !important; }
          .main-area { padding: 24px 16px 60px !important; }
          .hide-sm { display: none !important; }
        }
      `}</style>
    </div>
  );
}
